import React, { useEffect, useMemo, useState } from 'react';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import {
  BustaGo, GruppoTotali, OrdinaGo, RigaGo, RispostaRighe, RispostaTotali, dataIt, euro, num,
} from '../../lib/gestionaleOpen';

/**
 * Dettaglio di un gruppo dell'analisi GO (un cliente, un fornitore, una
 * commessa...) in un modale, invece che in coda alla pagina.
 *
 * Due livelli: prima l'elenco dei documenti (una riga per fattura/ordine, con
 * il suo totale), poi — al clic — le righe del singolo documento. Mostrare
 * subito tutte le righe di tutti i documenti mescolava centinaia di record
 * di fatture diverse, ed era illeggibile.
 */

interface Props {
  /** Titolo del modale: la controparte o il gruppo aperto */
  titolo: string;
  /** Filtri gia' completi (societa', vista, periodo, filtro del gruppo) */
  filtri: URLSearchParams;
  /** Filtro aggiuntivo lato browser, per i gruppi che l'API non sa filtrare (il mese) */
  filtroDocumenti?: (d: GruppoTotali) => boolean;
  /** Apre direttamente le righe di questo documento (tab "Da collegare") */
  documentoIniziale?: GruppoTotali;
  /** Mostra la colonna "Da attribuire" (solo sulle fatture) */
  conDaAttribuire?: boolean;
  onClose: () => void;
}

const LIMITE_DOCUMENTI = 5000;
const LIMITE_RIGHE = 1000;

export function nomeDocumento(d: GruppoTotali): string {
  return `${d.descrizione_documento ?? d.codice_documento ?? 'Documento'} n. ${d.numero_documento ?? '—'}`;
}

export default function ModaleDocumentiGo({
  titolo, filtri, filtroDocumenti, documentoIniziale, conDaAttribuire = false, onClose,
}: Props) {
  // Livello 1: elenco documenti (non serve se si parte da un documento)
  const [documenti, setDocumenti] = useState<GruppoTotali[] | null>(null);
  const [caricandoDocumenti, setCaricandoDocumenti] = useState(!documentoIniziale);
  const [erroreDocumenti, setErroreDocumenti] = useState('');

  // Livello 2: righe del documento scelto
  const [documento, setDocumento] = useState<GruppoTotali | null>(documentoIniziale ?? null);
  const [righe, setRighe] = useState<RigaGo[] | null>(null);
  const [caricandoRighe, setCaricandoRighe] = useState(false);
  const [erroreRighe, setErroreRighe] = useState('');

  const chiaveFiltri = filtri.toString();

  // Esc chiude il modale
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (documentoIniziale) return undefined;
    let annullato = false;
    (async () => {
      setCaricandoDocumenti(true);
      setErroreDocumenti('');
      try {
        const p = new URLSearchParams(chiaveFiltri);
        p.set('raggruppa', 'documento');
        p.set('limit', String(LIMITE_DOCUMENTI));
        // @api GET /api/bop-go/dati-commerciali/totali?societaId=1&vista=costi-fornitore&raggruppa=documento&fornitore=3446&anno=2026&limit=5000 -> {"success":true,"stato":"OK","data":{"count":"number","data":[{"codice":"number","codice_documento":"string","descrizione_documento":"string","numero_documento":"string","prima_data":"string","importo":"string","importo_con_commessa":"string","importo_senza_commessa":"string","righe":"number"}]}}
        const res = await apiFetch<BustaGo<RispostaTotali>>(`/api/bop-go/dati-commerciali/totali?${p.toString()}`);
        if (annullato) return;
        if (res.stato !== 'OK') {
          setErroreDocumenti(res.error || 'Dati GO non disponibili');
          setDocumenti([]);
          return;
        }
        setDocumenti(res.data?.data ?? []);
      } catch (err) {
        if (annullato) return;
        setErroreDocumenti(err instanceof Error ? err.message : 'Errore nel recupero dei documenti da GO');
        setDocumenti([]);
      } finally {
        if (!annullato) setCaricandoDocumenti(false);
      }
    })();
    return () => { annullato = true; };
  }, [chiaveFiltri, documentoIniziale]);

  useEffect(() => {
    if (!documento || documento.codice === null || documento.codice === undefined) { setRighe(null); return undefined; }
    let annullato = false;
    (async () => {
      setCaricandoRighe(true);
      setErroreRighe('');
      setRighe(null);
      try {
        const p = new URLSearchParams(chiaveFiltri);
        p.set('documento', String(documento.codice));
        p.set('ordina', 'data_asc' as OrdinaGo);
        p.set('limit', String(LIMITE_RIGHE));
        // I filtri di riga del gruppo (es. solo senza commessa) restano: si vede
        // la parte del documento che compone il totale del gruppo.
        // @api GET /api/bop-go/dati-commerciali/righe?societaId=1&vista=costi-fornitore&documento=3446&ordina=data_asc&limit=1000 -> {"success":true,"stato":"OK","data":{"count":"number","totale":"number","has_more":"boolean","data":[]}}
        const res = await apiFetch<BustaGo<RispostaRighe>>(`/api/bop-go/dati-commerciali/righe?${p.toString()}`);
        if (annullato) return;
        setRighe(res.data?.data ?? []);
        if (res.stato !== 'OK' && res.error) setErroreRighe(res.error);
      } catch (err) {
        if (annullato) return;
        setErroreRighe(err instanceof Error ? err.message : 'Errore nel recupero delle righe da GO');
      } finally {
        if (!annullato) setCaricandoRighe(false);
      }
    })();
    return () => { annullato = true; };
  }, [documento, chiaveFiltri]);

  const documentiVisibili = useMemo(
    () => (documenti ?? []).filter(d => !filtroDocumenti || filtroDocumenti(d)),
    [documenti, filtroDocumenti]
  );
  const totaleDocumenti = useMemo(
    () => documentiVisibili.reduce((s, d) => s + num(d.importo), 0),
    [documentiVisibili]
  );

  const colonneDocumenti = useMemo<BopExcelColumn<GruppoTotali>[]>(() => {
    const cols: BopExcelColumn<GruppoTotali>[] = [
      {
        key: 'data',
        header: 'Data',
        accessor: (d) => d.prima_data ?? '',
        render: (d) => dataIt(d.prima_data),
        width: '110px',
      },
      {
        key: 'documento',
        header: 'Documento',
        accessor: (d) => nomeDocumento(d),
        filter: { type: 'text' },
      },
      {
        key: 'righe',
        header: 'Righe',
        accessor: (d) => d.righe,
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '80px',
      },
      {
        key: 'importo',
        header: 'Importo',
        accessor: (d) => num(d.importo),
        render: (d) => euro(d.importo),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        filter: { type: 'number' },
        width: '140px',
      },
      {
        key: 'con_commessa',
        header: 'Su commessa',
        accessor: (d) => num(d.importo_con_commessa),
        render: (d) => euro(d.importo_con_commessa),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '140px',
      },
    ];
    if (conDaAttribuire) {
      cols.push({
        key: 'da_attribuire',
        header: 'Da attribuire',
        accessor: (d) => num(d.importo_senza_commessa),
        render: (d) => euro(d.importo_senza_commessa),
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
        width: '140px',
      });
    }
    return cols;
  }, [conDaAttribuire]);

  const colonneRighe = useMemo<BopExcelColumn<RigaGo>[]>(() => [
    { key: 'riga', header: 'Riga', accessor: (r) => r.riga, width: '70px' },
    { key: 'articolo', header: 'Articolo', accessor: (r) => r.codice_articolo ?? '', width: '140px' },
    { key: 'descrizione', header: 'Descrizione', accessor: (r) => r.DESCRIZIONE1 ?? '', filter: { type: 'text' } },
    {
      key: 'commessa',
      header: 'Commessa',
      accessor: (r) => r.codice_commessa ?? '',
      render: (r) => (r.codice_commessa
        ? <span className="bop-badge bop-badge-info">{r.codice_commessa}</span>
        : <span className="bop-badge bop-badge-warning">da attribuire</span>),
      width: '150px',
    },
    {
      key: 'origine',
      header: 'Da dove',
      accessor: (r) => etichettaOrigineCommessa(r.origine_commessa),
      width: '150px',
    },
    {
      key: 'importo',
      header: 'Importo',
      // importo_con_segno = IMPORTO × segno: le note di credito si sottraggono
      accessor: (r) => num(r.importo_con_segno ?? r.IMPORTO),
      render: (r) => euro(r.importo_con_segno ?? r.IMPORTO),
      cellClassName: 'bop-text-right',
      headerClassName: 'bop-text-right',
      width: '130px',
    },
  ], []);

  const totaleRighe = useMemo(
    () => (righe ?? []).reduce((s, r) => s + num(r.importo_con_segno ?? r.IMPORTO), 0),
    [righe]
  );

  const tornaAllElenco = !documentoIniziale && documento !== null;

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div
        className="bop-modal bop-modal--xl bop-dettaglio-go"
        role="dialog"
        aria-modal="true"
        aria-label={titolo}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bop-modal__header">
          <div className="bop-dettaglio-go__intestazione">
            <h2 className="bop-modal__title">{titolo}</h2>
            <div className="bop-dettaglio-go__sottotitolo">
              {documento ? nomeDocumento(documento) : 'Documenti del periodo'}
            </div>
          </div>
          <button className="bop-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>

        <div className="bop-modal__body">
          {tornaAllElenco && (
            <button className="bop-btn bop-btn-secondary bop-dettaglio-go__indietro" onClick={() => setDocumento(null)}>
              ← Tutti i documenti
            </button>
          )}

          {/* Livello 1 — elenco documenti */}
          {!documento && (
            <>
              {erroreDocumenti && <div className="bop-alert bop-alert--danger">{erroreDocumenti}</div>}
              {caricandoDocumenti && <div className="bop-empty-state">Lettura dei documenti…</div>}
              {!caricandoDocumenti && documenti && (
                <>
                  <div className="bop-dettaglio-go__riepilogo">
                    <span className="bop-dettaglio-go__riepilogo-voce">
                      <strong>{documentiVisibili.length}</strong> documenti
                    </span>
                    <span className="bop-dettaglio-go__riepilogo-voce">
                      Totale <strong>{euro(totaleDocumenti)}</strong>
                    </span>
                    <span className="bop-text-muted">Clic su un documento per vederne le righe.</span>
                  </div>
                  <BopExcelTable<GruppoTotali>
                    data={documentiVisibili}
                    columns={colonneDocumenti}
                    rowKey={(d) => String(d.codice)}
                    onRowClick={(d) => setDocumento(d)}
                    initialSort={{ key: 'data', dir: 'desc' }}
                    storageKey="dettaglio-go-documenti"
                    emptyState={<div className="bop-empty-state">Nessun documento</div>}
                  />
                </>
              )}
            </>
          )}

          {/* Livello 2 — righe del documento */}
          {documento && (
            <>
              <div className="bop-dettaglio-go__riepilogo">
                <span className="bop-dettaglio-go__riepilogo-voce">
                  Data <strong>{dataIt(documento.prima_data)}</strong>
                </span>
                <span className="bop-dettaglio-go__riepilogo-voce">
                  Importo <strong>{euro(documento.importo)}</strong>
                </span>
                <span className="bop-dettaglio-go__riepilogo-voce">
                  Su commessa <strong>{euro(documento.importo_con_commessa)}</strong>
                </span>
                {righe?.[0]?.data_competenza_bilancio && (
                  <span className="bop-dettaglio-go__riepilogo-voce" title={`Fonte: ${righe[0].fonte_competenza ?? '—'}`}>
                    Competenza <strong>{dataIt(righe[0].data_competenza_bilancio)}</strong>
                  </span>
                )}
              </div>
              {erroreRighe && <div className="bop-alert bop-alert--danger">{erroreRighe}</div>}
              {caricandoRighe && !righe && <div className="bop-empty-state">Lettura delle righe…</div>}
              {righe && (
                <>
                  <BopExcelTable<RigaGo>
                    data={righe}
                    columns={colonneRighe}
                    rowKey={(r) => `${r.progressivo}-${r.riga}-${r.id_movimento_commessa ?? ''}-${r.origine_commessa ?? ''}`}
                    loading={caricandoRighe}
                    storageKey="dettaglio-go-righe"
                    emptyState={<div className="bop-empty-state">Nessuna riga</div>}
                  />
                  <div className="bop-dettaglio-go__totale-righe">
                    {righe.length} righe · totale <strong>{euro(totaleRighe)}</strong>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Da dove viene la commessa di una riga di fattura (origine_commessa delle
 * viste GO). 'movimento contabile' = commessa messa sulla prima nota che
 * contabilizza la fattura, non sulla riga (es. COBROWSE 380029).
 */
export function etichettaOrigineCommessa(origine: string | null | undefined): string {
  switch (origine) {
    case 'movimento': return 'movimento commessa';
    case 'movimento contabile': return 'prima nota';
    case 'movimento ddt': return 'dal DDT';
    case 'riga': return 'riga fattura';
    case 'testata': return 'testata fattura';
    case 'residuo': return 'non attribuita';
    default: return '—';
  }
}

/** Usato dai test e dall'analisi per il gruppo "mese": 2026-03, 202603 o 3 */
export function stessoMese(codiceGruppo: string | null, data: string | null): boolean {
  if (!codiceGruppo || !data) return true;
  const soloCifre = codiceGruppo.split('-').join('');
  const meseData = data.slice(0, 7).split('-').join(''); // AAAAMM
  if (soloCifre.length === 6) return meseData === soloCifre;
  const n = parseInt(soloCifre, 10);
  if (n >= 1 && n <= 12) return parseInt(data.slice(5, 7), 10) === n;
  return true;
}
